import { defineComponent as _defineComponent } from 'vue';
import { library } from "@fortawesome/fontawesome-svg-core";
import { faCodeBranch } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import {} from "@vueuse/core";
import BootstrapVue from "bootstrap-vue";
import Vue, { computed, reactive, ref } from "vue";
import { getGalaxyInstance } from "@/app";
import { DatatypesMapperModel } from "@/components/Datatypes/model";
import { useNodePosition } from "@/components/Workflow/Editor/composables/useNodePosition";
import WorkflowIcons from "@/components/Workflow/icons";
import { useWorkflowStores } from "@/composables/workflowStores";
import { useWorkflowNodeInspectorStore } from "@/stores/workflowNodeInspectorStore";
import { composedPartialPath, isClickable } from "@/utils/dom";
import { ToggleStepSelectedAction } from "./Actions/stepActions";
import LoadingSpan from "@/components/LoadingSpan.vue";
import DraggableWrapper from "@/components/Workflow/Editor/DraggablePan.vue";
import NodeInput from "@/components/Workflow/Editor/NodeInput.vue";
import NodeInvocationText from "@/components/Workflow/Editor/NodeInvocationText.vue";
import NodeOutput from "@/components/Workflow/Editor/NodeOutput.vue";
import Recommendations from "@/components/Workflow/Editor/Recommendations.vue";
export default /*#__PURE__*/ _defineComponent({
    __name: 'Node',
    props: {
        id: { type: Number, required: true },
        contentId: { type: String, default: null },
        name: { type: String, default: null },
        step: { type: Object, required: true },
        datatypesMapper: { type: DatatypesMapperModel, required: true },
        activeNodeId: {
            type: null,
            required: true,
            validator: (v) => typeof v === "number" || v === null,
        },
        rootOffset: { type: Object, required: true },
        scroll: { type: Object, required: true },
        scale: { type: Number, default: 1 },
        highlight: { type: Boolean, default: false },
        isInvocation: { type: Boolean, default: false },
        readonly: { type: Boolean, default: false },
    },
    emits: [
        "onRemove",
        "onActivate",
        "onChange",
        "onCreate",
        "onClone",
        "onUpdateStepPosition",
        "pan-by",
        "onDragConnector",
        "stopDragging",
    ],
    setup(__props, { emit }) {
        const props = __props;
        Vue.use(BootstrapVue);
        library.add(faCodeBranch);
        const popoverShow = ref(false);
        const popoverId = computed(() => `popover-${props.id}`);
        const scrolledTo = ref(false);
        function remove() {
            emit("onRemove", props.id);
        }
        const position = computed(() => ({ x: props.step.position?.left ?? 0, y: props.step.position?.top ?? 0 }));
        const el = ref(null);
        const elHtml = computed(() => el.value?.$el ?? null);
        const postJobActions = computed(() => props.step.post_job_actions || {});
        const workflowOutputs = computed(() => props.step.workflow_outputs || []);
        const { connectionStore, stateStore, stepStore, undoRedoStore } = useWorkflowStores();
        const isLoading = computed(() => Boolean(stateStore.getStepLoadingState(props.id)?.loading));
        useNodePosition(elHtml, props.id, stateStore, computed(() => props.scale));
        const title = computed(() => props.step.label || props.step.name);
        const idString = computed(() => `wf-node-step-${props.id}`);
        const showRule = computed(() => props.step.inputs?.length > 0 && props.step.outputs?.length > 0);
        const iconClass = computed(() => `icon fa fa-fw ${WorkflowIcons[props.step.type]}`);
        const canClone = computed(() => props.step.type !== "subworkflow"); // Why ?
        const isEnabled = getGalaxyInstance().config.enable_tool_recommendations; // getGalaxyInstance is not reactive
        const isActive = computed(() => props.id === props.activeNodeId);
        const classes = computed(() => {
            return {
                "node-on-scroll-to": scrolledTo.value,
                "node-highlight": props.highlight || isActive.value,
                "is-active": isActive.value,
                "node-multi-selected": stateStore.getStepMultiSelected(props.id),
            };
        });
        const style = computed(() => {
            return { top: props.step.position.top + "px", left: props.step.position.left + "px" };
        });
        const errors = computed(() => props.step.errors || stateStore.getStepLoadingState(props.id)?.error);
        const headerClass = computed(() => {
            return {
                ...invocationStep.value.headerClass,
                "cursor-pointer": props.isInvocation,
                "node-header": !props.isInvocation || invocationStep.value.headerClass === undefined,
                "cursor-move": !props.readonly && !props.isInvocation,
            };
        });
        const inputs = computed(() => {
            const connections = connectionStore.getConnectionsForStep(props.id);
            const extraStepInputs = stepStore.getStepExtraInputs(props.id);
            const stepInputs = [...extraStepInputs, ...(props.step.inputs || [])];
            const unknownInputs = [];
            connections.forEach((connection) => {
                if (connection.input.stepId == props.id && !stepInputs.find((input) => input.name === connection.input.name)) {
                    unknownInputs.push(connection.input.name);
                }
            });
            const invalidInputNames = [...new Set(unknownInputs)];
            const invalidInputTerminalSource = invalidInputNames.map((name) => {
                return { name, optional: false, extensions: [], valid: false, input_type: "dataset" };
            });
            return [...stepInputs, ...invalidInputTerminalSource];
        });
        const invalidOutputs = computed(() => {
            const connections = connectionStore.getConnectionsForStep(props.id);
            const invalidConnections = connections.filter((connection) => connection.output.stepId == props.id &&
                !props.step.outputs.find((output) => output.name === connection.output.name));
            const invalidOutputNames = [...new Set(invalidConnections.map((connection) => connection.output.name))];
            return invalidOutputNames.map((name) => {
                return { name, optional: false, datatypes: [], valid: false };
            });
        });
        const invocationStep = computed(() => props.step);
        const outputs = computed(() => {
            return [...props.step.outputs, ...invalidOutputs.value];
        });
        function onDragConnector(dragPosition, terminal) {
            emit("onDragConnector", dragPosition, terminal);
        }
        const mouseMovementThreshold = 9;
        const singleClickTimeout = 800;
        const doubleClickTimeout = 500;
        let mouseDownTime = 0;
        let doubleClickTime = 0;
        let movementDistance = 0;
        let lastPosition = null;
        const inspectorStore = useWorkflowNodeInspectorStore();
        function onPointerDown() {
            mouseDownTime = Date.now();
        }
        function onPointerUp(e) {
            const path = composedPartialPath(e);
            const unclickable = path.every((target) => !isClickable(target));
            if (!unclickable) {
                return;
            }
            const mouseUpTime = Date.now();
            const clickTime = mouseUpTime - mouseDownTime;
            if (clickTime <= singleClickTimeout && movementDistance <= mouseMovementThreshold) {
                makeActive();
            }
            const timeBetweenClicks = mouseUpTime - doubleClickTime;
            if (timeBetweenClicks < doubleClickTimeout) {
                inspectorStore.setMaximized(props.step, true);
            }
            doubleClickTime = Date.now();
            lastPosition = null;
            movementDistance = 0;
        }
        function onMoveTo(position) {
            if (lastPosition) {
                movementDistance += Math.abs(position.x - lastPosition.x);
                movementDistance += Math.abs(position.y - lastPosition.y);
            }
            lastPosition = position;
            emit("onUpdateStepPosition", props.id, {
                top: position.y + props.scroll.y.value / props.scale,
                left: position.x + props.scroll.x.value / props.scale,
            });
        }
        function onPanBy(panBy) {
            emit("pan-by", panBy);
        }
        function onStopDragging() {
            emit("stopDragging");
        }
        function onChange() {
            emit("onChange");
        }
        function onCreate(contentId, name) {
            emit("onCreate", contentId, name);
            popoverShow.value = false;
        }
        function onClone() {
            emit("onClone", props.id);
        }
        function makeActive() {
            emit("onActivate", props.id);
        }
        function toggleSelected() {
            undoRedoStore.applyAction(new ToggleStepSelectedAction(stateStore, stepStore, props.id));
        }
        return { __sfc: true, props, emit, popoverShow, popoverId, scrolledTo, remove, position, el, elHtml, postJobActions, workflowOutputs, connectionStore, stateStore, stepStore, undoRedoStore, isLoading, title, idString, showRule, iconClass, canClone, isEnabled, isActive, classes, style, errors, headerClass, inputs, invalidOutputs, invocationStep, outputs, onDragConnector, mouseMovementThreshold, singleClickTimeout, doubleClickTimeout, mouseDownTime, doubleClickTime, movementDistance, lastPosition, inspectorStore, onPointerDown, onPointerUp, onMoveTo, onPanBy, onStopDragging, onChange, onCreate, onClone, makeActive, toggleSelected, FontAwesomeIcon, reactive, LoadingSpan, DraggableWrapper, NodeInput, NodeInvocationText, NodeOutput, Recommendations };
    }
});
